import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Activity, Calendar, Video, AlertTriangle,
  MapPin, Stethoscope, CheckCircle, XCircle,
  Shield, Award, Clock, ArrowRight, Sparkles, ShieldCheck, IdCard, Loader2, CheckCircle2, Globe, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { AvailabilityToggle } from '../components/brand/AvailabilityToggle';
import { DashboardShell } from '../components/brand/DashboardShell';
import { MorningTodayWidget } from '../components/brand/MorningTodayWidget';
import { clinicianNavItems } from '../components/brand/navItems';
import { isFeatureLive } from '../lib/caraloom_v2_ia';
import { BentoGrid, BentoTile, Counter, RingScore, Sparkline, LiveDot, PhotoEmptyState, TileHeader } from '../components/brand/BentoWidgets';
import { ProfilePhotoUpload } from '../components/brand/ProfilePhotoUpload';
import { ClinicianInsuranceCard } from '../components/brand/InsuranceCards';
import { ClinicianAgencyCard, ClinicianEngagementsInbox } from '../components/brand/AgencyCards';
import { PracticeProfileCard } from '../components/brand/PracticeProfileCard';
import { CareJourneyTracker } from '../components/brand/CareJourneyTracker';
import { ClinicianShareCard } from '../components/brand/ShareProfile';
import { CredentialingPipelineWidget } from '../components/brand/CredentialingPipelineWidget';
import { TaxReserveWidget } from '../components/brand/TaxReserveWidget';
import { SettlementHistoryWidget } from '../components/brand/SettlementHistoryWidget';
import { ReadmissionRiskWidget } from '../components/brand/ReadmissionRiskWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_IMG = 'https://static.prod-images.emergentagent.com/jobs/d66875ad-9bb9-40cd-9a51-63fb813a049d/images/425acea9f51be82774585541d81ccbfbcd5a240ae13644bde8dd1653a6c1057d.png';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = ['8a', '10a', '12p', '2p', '4p', '6p'];

export default function ClinicianDashboard() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyingNpi, setVerifyingNpi] = useState(false);
  const [profileScore, setProfileScore] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [bankAvailable, setBankAvailable] = useState(0);

  useEffect(() => { fetchData(); }, []);

  const loadScore = async () => {
    try {
      const { data } = await axios.get(`${API}/clinician/profile-score`, getAuthHeader());
      setProfileScore(data);
    } catch (e) { /* non-blocking */ }
  };

  const fetchData = async () => {
    try {
      const [profileRes, matchesRes, appointmentsRes, earningsRes] = await Promise.all([
        axios.get(`${API}/clinician/profile`, getAuthHeader()),
        axios.get(`${API}/clinician/matches`, getAuthHeader()),
        axios.get(`${API}/appointments`, getAuthHeader()),
        axios.get(`${API}/clinician/earnings`, getAuthHeader()),
      ]);
      setProfile(profileRes.data);
      setMatches(matchesRes.data);
      setAppointments(appointmentsRes.data);
      setEarningsData(earningsRes.data);
      loadScore();
      try {
        const bankRes = await axios.get(`${API}/clinician/banking`, getAuthHeader());
        setBankAvailable(bankRes.data?.balance?.available || 0);
      } catch (e) { /* non-blocking */ }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToAnchor = () => {
    const el = document.getElementById('profile');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const verifyNpi = async () => {
    setVerifyingNpi(true);
    try {
      const { data } = await axios.post(`${API}/clinician/verify-npi`, {}, getAuthHeader());
      setProfile((p) => ({ ...(p || {}), npi_verified: data.verified, npi_verification: data }));
      loadScore();
      if (data.verified) toast.success('NPI verified against the NPPES registry ✓');
      else toast.warning(data.message || 'NPI could not be verified — check your NPI.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not verify NPI right now.');
    } finally {
      setVerifyingNpi(false);
    }
  };

  const respondToMatch = async (matchId, status) => {
    try {
      await axios.put(`${API}/clinician/matches/${matchId}/respond`, { status }, getAuthHeader());
      toast.success(`Match ${status}!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to respond to match');
    }
  };

  const pendingMatches = matches.filter(m => m.status === 'pending');
  const acceptedMatches = matches.filter(m => m.status === 'accepted');

  const navItems = clinicianNavItems({
    pathname: location.pathname,
    pendingMatchesCount: pendingMatches.length,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Real earnings (from paid transactions) + a visit-activity heatmap derived from real appointments.
  const weekEarnings = earningsData?.week || [];
  const weekTotal = earningsData?.week_total || 0;
  const HOUR_VALUES = [8, 10, 12, 14, 16, 18];
  const heatmap = DAYS.map((d) => HOURS.map((h) => ({ d, h, intensity: 0 })));
  appointments.forEach((a) => {
    const dt = a.scheduled_date ? new Date(`${a.scheduled_date}T00:00:00`) : null;
    if (!dt || isNaN(dt)) return;
    const dow = (dt.getDay() + 6) % 7; // Mon=0 .. Sun=6
    const hr = parseInt((a.scheduled_time || '').split(':')[0], 10);
    if (isNaN(hr)) return;
    let bi = 0, best = Infinity;
    HOUR_VALUES.forEach((hv, i) => { const diff = Math.abs(hv - hr); if (diff < best) { best = diff; bi = i; } });
    if (heatmap[dow]?.[bi]) heatmap[dow][bi].intensity = Math.min(3, heatmap[dow][bi].intensity + 1);
  });

  // Clinician activation journey — derived from real profile/match/appointment state.
  const journeySteps = [
    { key: 'verify', label: 'Verify license', hint: 'We confirm your credentials', cta: 'Check verification status', done: !!profile?.license_verified },
    { key: 'profile', label: 'Build profile', hint: 'Photo, bio, specialties & rate', cta: 'Complete your profile', done: !!profile?.ai_assessment_complete },
    { key: 'matched', label: 'Get matched', hint: 'Accept your first case', cta: 'Review case requests', done: acceptedMatches.length > 0 },
    { key: 'booking', label: 'First booking', hint: 'Confirm a visit', cta: 'See your schedule', done: appointments.length > 0 },
    { key: 'paid', label: 'Get paid', hint: 'Settles on visit verification', cta: 'Open billing', done: appointments.some((a) => a.status === 'completed') },
  ];
  const onJourneyStep = (s) => {
    if (s.key === 'paid') { navigate('/clinician/billing'); return; }
    const anchors = { verify: 'profile', profile: 'profile', matched: 'cases', booking: 'schedule' };
    const el = document.getElementById(anchors[s.key]);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <DashboardShell
      navItems={navItems}
      title={`Welcome, ${user?.first_name || 'Clinician'}`}
      subtitle={`Your independent practice${profile?.license_type ? ` · ${profile.license_type}` : ''} · ${pendingMatches.length} request${pendingMatches.length === 1 ? '' : 's'} waiting`}
      rightSlot={<AvailabilityToggle />}
      testId="clinician-dashboard"
    >
      {/* Morning Today widget — Blueprint #18 ritual landing */}
      <div className="mb-5">
        <MorningTodayWidget getAuthHeader={getAuthHeader} />
      </div>

      {/* Alerts — banking nudge is v2-tier (Phase 7 slim: flags off at v1) */}
      {bankAvailable > 0 && isFeatureLive('workforce.banking') && (
        <Link to="/clinician/banking" className="block mb-4 group" data-testid="instant-pay-nudge">
          <div className="cn-card p-4 sm:p-5 flex items-center gap-4 border-cn-success/30 bg-gradient-to-br from-cn-success/8 to-transparent transition-colors group-hover:border-cn-success/50">
            <div className="w-12 h-12 rounded-2xl bg-cn-success/15 flex items-center justify-center text-cn-success shrink-0">
              <Wallet className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-cn-text dark:text-white">
                <span data-testid="instant-pay-amount">${bankAvailable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> available to withdraw
              </p>
              <p className="text-sm text-cn-text-muted">Cash out to your bank — standard (free) or instant. Money you&apos;ve already earned.</p>
            </div>
            <span className="cn-btn cn-btn-primary !py-2 shrink-0" data-testid="instant-pay-cta">
              Instant pay <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      )}
      {!profile?.ai_assessment_complete && (
        <div className="mb-4 cn-card p-4 flex flex-col sm:flex-row sm:items-center gap-4 border-cn-accent/40 bg-cn-accent/5" data-testid="profile-nudge">
          <div className="w-12 h-12 rounded-2xl bg-cn-accent/15 flex items-center justify-center text-cn-accent">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-cn-text dark:text-white">Complete your profile assessment</p>
            <p className="text-sm text-cn-text-muted">Higher-fit cases unlock once we know your skills.</p>
          </div>
          <Link to="/clinician/assessment">
            <Button className="cn-btn cn-btn-accent" data-testid="start-clinician-assessment-btn">
              Complete <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}
      {!profile?.license_verified && (
        <div className="mb-6 cn-card p-4 flex items-center gap-4 border-cn-primary/30 bg-cn-primary/5" data-testid="verification-banner">
          <div className="w-11 h-11 rounded-2xl bg-cn-primary/15 flex items-center justify-center text-cn-primary">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-cn-text dark:text-white">License verification in review</p>
            <p className="text-sm text-cn-text-muted">We&apos;ll notify you the moment your credentials clear.</p>
          </div>
          <Badge className="badge-info">In Review</Badge>
        </div>
      )}

      {profileScore && !profileScore.complete && (
        <div className="mb-6 cn-card p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-5 border-cn-secondary/30 bg-gradient-to-br from-cn-secondary/8 to-transparent" data-testid="profile-strength-card">
          <div className="flex-shrink-0 flex flex-col items-center">
            <RingScore value={profileScore.score} size={104} label="Strength" />
            <span className="mt-1.5 text-[11px] font-bold uppercase tracking-wider text-cn-secondary" data-testid="profile-strength-tier">{profileScore.tier}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cn-secondary" />
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Boost your profile & search ranking</h3>
            </div>
            <p className="text-sm text-cn-text-muted mt-1">
              Your profile now appears in Google via our sitemap. Complete these to win more families &amp; discharge-planner referrals.
            </p>
            <div className="mt-3 space-y-1.5">
              {profileScore.checks.filter((c) => !c.done).slice(0, 4).map((c) => (
                <button
                  key={c.key}
                  onClick={() => (c.key === 'npi' && profile?.npi ? verifyNpi() : scrollToAnchor())}
                  className="w-full flex items-center gap-2.5 text-left group"
                  data-testid={`profile-strength-item-${c.key}`}
                >
                  <span className="w-5 h-5 rounded-full border-2 border-cn-secondary/40 flex-shrink-0 group-hover:bg-cn-secondary/10 transition-colors" />
                  <span className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-cn-text dark:text-white">{c.label}</span>
                    <span className="text-xs text-cn-text-muted"> · +{c.weight} pts</span>
                  </span>
                  <ArrowRight className="w-4 h-4 text-cn-text-muted group-hover:text-cn-secondary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {profileScore?.complete && (
        <div className="mb-6 cn-card p-4 flex items-center gap-3 border-cn-success/30 bg-cn-success/5" data-testid="profile-strength-complete">
          <CheckCircle2 className="w-5 h-5 text-cn-success flex-shrink-0" />
          <p className="text-sm font-medium text-cn-text dark:text-white">Your profile is fully optimized for search &amp; bookings. Nicely done!</p>
        </div>
      )}

      <CareJourneyTracker steps={journeySteps} onStepClick={onJourneyStep} eyebrow="Set up your practice" completeLabel="Your practice is live" />

      <BentoGrid>
        {/* HERO — Earnings & Cases */}
        <BentoTile span="md:col-span-8 lg:col-span-7" rowSpan="lg:row-span-2" className="p-7 sm:p-9 text-white border-transparent" hover={false} testId="earnings-hero"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #334155 100%)' }}>
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-cn-accent/25 blur-3xl" />
          <div className="absolute -left-12 -bottom-12 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
          <div className="relative h-full flex flex-col">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-white/70 font-bold">This week</p>
                <h2 className="cn-display text-3xl sm:text-4xl mt-2 max-w-md leading-tight text-white">Your practice, in flow.</h2>
              </div>
              <LiveDot color="#fff" label="Available" />
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4">
              {isFeatureLive('workforce.payouts') ? (
                <Link to="/clinician/payouts" className="rounded-2xl bg-white/10 backdrop-blur p-4 block hover:bg-white/15 transition-colors group" data-testid="earnings-tile-link">
                  <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold flex items-center justify-between">Earnings (wk) <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></p>
                  <p className="text-3xl font-display font-extrabold mt-1" data-testid="clinician-week-earnings">$<Counter value={weekTotal} /></p>
                  <div className="mt-2 -mx-1"><Sparkline data={weekEarnings.length ? weekEarnings : [0, 0, 0, 0, 0, 0, 0]} stroke="#fff" fill="rgba(255,255,255,0.18)" height={28} /></div>
                </Link>
              ) : (
                <Link to="/spine/clinician" className="rounded-2xl bg-white/10 backdrop-blur p-4 block hover:bg-white/15 transition-colors group" data-testid="work-tile-link">
                  <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold flex items-center justify-between">Open offers <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" /></p>
                  <p className="text-3xl font-display font-extrabold mt-1"><Counter value={pendingMatches.length} /></p>
                  <p className="text-xs text-white/70 mt-1">First to accept wins</p>
                </Link>
              )}
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Active cases</p>
                <p className="text-3xl font-display font-extrabold mt-1"><Counter value={acceptedMatches.length} /></p>
                <p className="text-xs text-white/70 mt-1">{appointments.length} appts scheduled</p>
              </div>
              <div className="rounded-2xl bg-white/10 backdrop-blur p-4">
                <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Competency</p>
                <p className="text-3xl font-display font-extrabold mt-1"><Counter value={profile?.competency_score || 0} /><span className="text-base text-white/60">/100</span></p>
                <p className="text-xs text-white/70 mt-1">{profile?.years_of_experience || 0} yrs exp</p>
              </div>
            </div>

            <div className="mt-auto pt-7 flex flex-wrap items-center gap-3">
              <Link to="/clinician/offers">
                <Button className="cn-btn !bg-white !text-cn-primary hover:!bg-cn-bg" data-testid="hero-offers-btn">
                  Review Offers <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/clinician/coach">
                <Button className="cn-btn !bg-white/15 hover:!bg-white/25 !text-white border border-white/20" data-testid="hero-coach-btn">
                  <Sparkles className="w-4 h-4" /> Practice Coach
                </Button>
              </Link>
            </div>
          </div>
        </BentoTile>

        {/* Pending requests counter */}
        <BentoTile span="md:col-span-4 lg:col-span-5" className="p-6 bg-cn-accent/8 border-cn-accent/25" testId="pending-requests-tile">
          <TileHeader eyebrow="Inbox" title="Pending requests" action={<LiveDot color="var(--cn-accent)" label="New" />} />
          <div className="mt-4 flex items-end gap-4">
            <p className="font-display font-extrabold text-5xl text-cn-text dark:text-white leading-none">
              <Counter value={pendingMatches.length} />
            </p>
            <p className="text-xs text-cn-text-muted pb-1">awaiting your response</p>
          </div>
          <a href="#cases">
            <Button className="cn-btn cn-btn-accent mt-3 w-full !py-2" data-testid="view-cases-btn">
              Open Case Requests
            </Button>
          </a>
        </BentoTile>

        {/* Visit activity heatmap (derived from real appointments) */}
        <BentoTile span="md:col-span-4 lg:col-span-5" className="p-6" testId="availability-heatmap">
          <TileHeader eyebrow="Your week" title="Visit activity" />
          <div className="mt-4 grid gap-1.5" style={{ gridTemplateColumns: `auto repeat(${HOURS.length}, 1fr)` }}>
            <div />
            {HOURS.map((h) => <div key={h} className="text-[10px] text-cn-text-muted text-center font-semibold">{h}</div>)}
            {heatmap.map((row, ri) => (
              <React.Fragment key={ri}>
                <div className="text-[10px] text-cn-text-muted font-semibold flex items-center">{DAYS[ri]}</div>
                {row.map((cell, ci) => (
                  <div key={ci} className="aspect-square rounded-md transition-transform hover:scale-110"
                    style={{ background: `rgba(44,85,69,${0.1 + cell.intensity * 0.18})` }}
                    title={`${cell.d} ${cell.h}`}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </BentoTile>

        {/* Competency ring */}
        <BentoTile span="md:col-span-4 lg:col-span-3" className="p-6 flex flex-col items-center justify-center" testId="competency-tile">
          <p className="cn-eyebrow">Competency</p>
          <div className="mt-3"><RingScore value={profile?.competency_score || 0} size={120} label="Score" /></div>
          <p className="text-xs text-cn-text-muted mt-3 text-center">Updated from your matches & reviews</p>
        </BentoTile>

        {/* Credentials row */}
        <BentoTile span="md:col-span-8 lg:col-span-9" className="p-6" testId="credentials-tile">
          <TileHeader eyebrow="Profile" title="Credentials at a glance" action={
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/clinician/migrate">
                <Button className="cn-btn cn-btn-ghost !py-2" data-testid="import-from-website-link"><Globe className="w-4 h-4" /> Import from website</Button>
              </Link>
              <a href="#profile">
                <Button className="cn-btn cn-btn-ghost !py-2" data-testid="edit-practice-profile-link">Edit profile</Button>
              </a>
            </div>
          } />
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/60">
              <Stethoscope className="w-5 h-5 text-cn-primary mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">License</p>
              <p className="font-display font-bold text-cn-text dark:text-white">{profile?.license_type || 'Not set'}</p>
            </div>
            <div className="p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/60">
              <Shield className={`w-5 h-5 mb-2 ${profile?.license_verified ? 'text-cn-success' : 'text-cn-warning'}`} />
              <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">Verification</p>
              <p className={`font-display font-bold ${profile?.license_verified ? 'text-cn-success' : 'text-cn-warning'}`}>
                {profile?.license_verified ? 'Verified' : 'Pending'}
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/60">
              <Clock className="w-5 h-5 text-cn-secondary mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">Experience</p>
              <p className="font-display font-bold text-cn-text dark:text-white">{profile?.years_of_experience || 0} yrs</p>
            </div>
            <div className="p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/60">
              <Award className="w-5 h-5 text-cn-accent mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">Specialties</p>
              <p className="font-display font-bold text-cn-text dark:text-white">{profile?.specialties?.length || 0}</p>
            </div>
          </div>
          {(profile?.specialties?.length > 0 || profile?.certifications?.length > 0) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {profile?.specialties?.map((s, i) => (
                <Badge key={`s${i}`} className="bg-cn-primary/10 text-cn-primary border border-cn-primary/20">{s}</Badge>
              ))}
              {profile?.certifications?.map((c, i) => (
                <Badge key={`c${i}`} variant="outline" className="border-cn-border">{c}</Badge>
              ))}
              {profile?.available_for_telehealth && (
                <Badge className="bg-cn-secondary/15 text-cn-secondary border border-cn-secondary/30">
                  <Video className="w-3 h-3 mr-1" /> Telehealth
                </Badge>
              )}
              {profile?.available_for_in_person && (
                <Badge className="bg-cn-accent/15 text-cn-accent border border-cn-accent/30">
                  <MapPin className="w-3 h-3 mr-1" /> In-Person
                </Badge>
              )}
            </div>
          )}

          {/* NPI verification — automated against the public NPPES registry */}
          <div className="mt-4 p-4 rounded-2xl border border-cn-border/60 bg-cn-bg/40" data-testid="npi-verification-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <IdCard className={`w-5 h-5 flex-shrink-0 ${profile?.npi_verified ? 'text-sky-600' : 'text-cn-text-muted'}`} />
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-cn-text-muted font-bold">NPI verification</p>
                  {profile?.npi ? (
                    <p className="text-sm text-cn-text dark:text-white" data-testid="npi-status-text">
                      {profile?.npi_verified
                        ? <span className="font-semibold text-sky-700 dark:text-sky-400">Verified via NPPES ✓</span>
                        : <span className="text-cn-text-muted">{profile?.npi_verification?.message || 'Not yet verified'}</span>}
                      <span className="text-cn-text-muted"> · NPI {profile.npi}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-cn-text-muted">Add your NPI in your profile below to enable automated verification.</p>
                  )}
                </div>
              </div>
              {profile?.npi && (
                <Button onClick={verifyNpi} disabled={verifyingNpi} className="cn-btn cn-btn-ghost !py-2 flex-shrink-0" data-testid="verify-npi-btn">
                  {verifyingNpi ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : (profile?.npi_verification ? 'Re-check NPI' : 'Verify my NPI')}
                </Button>
              )}
            </div>
          </div>
        </BentoTile>

        {/* Shareable public profile */}
        <BentoTile span="col-span-full" className="p-6 sm:p-7" id="share" testId="clinician-share-tile">
          <ClinicianShareCard profile={profile} />
        </BentoTile>

        {/* Practice profile editor (public details + NPI + clinical focus → badges) */}
        <BentoTile span="col-span-full lg:col-span-7" className="p-6 sm:p-7" id="profile" testId="clinician-practice-profile-tile">
          <PracticeProfileCard getAuthHeader={getAuthHeader} onSaved={fetchData} />
        </BentoTile>

        {/* Public profile photo */}
        <BentoTile span="col-span-full lg:col-span-5" className="p-6 sm:p-7" id="photo" testId="clinician-photo-tile">
          <TileHeader eyebrow="Profile" title="Your profile photo" />
          <p className="text-sm text-cn-text-muted mt-2">A warm, professional headshot helps families choose you — shown on your public profile and in search.</p>
          <div className="mt-5">
            <ProfilePhotoUpload
              currentUrl={profile?.photo_url}
              onUploaded={(url) => setProfile((p) => ({ ...(p || {}), photo_url: url }))}
              label="Public profile photo"
              hint="JPG, PNG or WebP · up to 5 MB"
              testId="clinician-photo-upload"
            />
          </div>
        </BentoTile>

        {/* Insurance acceptance */}
        <BentoTile span="col-span-full lg:col-span-5" className="p-6 sm:p-7" id="insurance" testId="clinician-insurance-tile">
          <ClinicianInsuranceCard getAuthHeader={getAuthHeader} />
        </BentoTile>

        {/* Income Engine — Credentialing + Taxes + Settlements (Feb 2026 pitch-parity row).
            These three tiles together back the "we're your operating system" promise
            so the modules the clinician is paying for are visible on day one. */}
        <BentoTile span="col-span-full lg:col-span-6" className="p-6 sm:p-7" id="credentialing" testId="clinician-credentialing-tile">
          <CredentialingPipelineWidget getAuthHeader={getAuthHeader} />
        </BentoTile>

        <BentoTile span="col-span-full lg:col-span-6" className="p-6 sm:p-7" id="tax-reserve" testId="clinician-tax-reserve-tile">
          <TaxReserveWidget getAuthHeader={getAuthHeader} />
        </BentoTile>

        <BentoTile span="col-span-full lg:col-span-6" className="p-6 sm:p-7" id="settlements" testId="clinician-settlements-tile">
          <SettlementHistoryWidget getAuthHeader={getAuthHeader} />
        </BentoTile>

        {/* Readmission Risk Engine — NEWS2 + LACE 30-day watchlist (Feb 2026).
            Lives next to the Income Engine row because the same clinician who
            wants paid quickly also wants to see who's at risk of deterioration
            before they walk in for the next visit. */}
        <BentoTile span="col-span-full lg:col-span-6" className="p-6 sm:p-7" id="readmission-risk" testId="clinician-readmission-tile">
          <ReadmissionRiskWidget getAuthHeader={getAuthHeader} />
        </BentoTile>

        {/* Agency / org staffing opt-in */}
        <BentoTile span="col-span-full lg:col-span-7" className="p-6 sm:p-7" id="agency" testId="clinician-agency-tile">
          <ClinicianAgencyCard getAuthHeader={getAuthHeader} />
        </BentoTile>

        {/* Agency engagement requests inbox */}
        <BentoTile span="col-span-full" className="p-6 sm:p-7" id="engagements" testId="clinician-engagements-tile">
          <ClinicianEngagementsInbox getAuthHeader={getAuthHeader} />
        </BentoTile>

        {/* Case requests stack */}
        <BentoTile span="col-span-full lg:col-span-8" className="p-6 sm:p-7" id="cases" testId="case-requests-section">
          <TileHeader eyebrow="Incoming" title="Case requests" action={
            <Badge className="bg-cn-accent/15 text-cn-accent">{pendingMatches.length} waiting</Badge>
          } />
          {pendingMatches.length === 0 ? (
            <div className="mt-5">
              <PhotoEmptyState
                image={EMPTY_IMG}
                title="No pending requests"
                body={profile?.ai_assessment_complete ? 'New cases land here. Keep your profile updated to attract the right fits.' : 'Complete your profile to start receiving matches.'}
                testId="cases-empty"
              />
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              {pendingMatches.slice(0, 3).map((match, index) => (
                <div key={match.id} className={`p-5 rounded-2xl border border-cn-border/70 hover:border-cn-primary/40 transition-all animate-fade-in-up stagger-${(index % 5) + 1}`} data-testid={`case-request-${index}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-2xl bg-cn-accent/15 text-cn-accent flex items-center justify-center font-bold">
                          {match.patient?.first_name?.[0]}{match.patient?.last_name?.[0]}
                        </div>
                        <div>
                          <h4 className="font-semibold text-cn-text dark:text-white">{match.patient?.first_name} {match.patient?.last_name}</h4>
                          <p className="text-xs text-cn-text-muted flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Location on file
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="px-3 py-2 rounded-xl bg-cn-bg/60 border border-cn-border/40">
                          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Care level</p>
                          <p className="text-xs font-bold text-cn-text dark:text-white mt-0.5 truncate">{match.patient_profile?.recommended_care_level || 'N/A'}</p>
                        </div>
                        <div className="px-3 py-2 rounded-xl bg-cn-bg/60 border border-cn-border/40">
                          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Complexity</p>
                          <p className="text-xs font-bold text-cn-text dark:text-white mt-0.5">{match.patient_profile?.care_complexity_score || '--'}/100</p>
                        </div>
                        <div className={`px-3 py-2 rounded-xl border ${match.patient_profile?.urgency_level === 'High Risk' ? 'bg-cn-accent/10 border-cn-accent/30' : 'bg-cn-bg/60 border-cn-border/40'}`}>
                          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Urgency</p>
                          <p className={`text-xs font-bold mt-0.5 ${match.patient_profile?.urgency_level === 'High Risk' ? 'text-cn-accent' : 'text-cn-text dark:text-white'}`}>{match.patient_profile?.urgency_level || 'N/A'}</p>
                        </div>
                        <div className="px-3 py-2 rounded-xl bg-cn-bg/60 border border-cn-border/40">
                          <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Type</p>
                          <p className="text-xs font-bold text-cn-text dark:text-white mt-0.5 capitalize truncate">{match.patient_profile?.preferred_care_type?.replace('_', ' ') || 'N/A'}</p>
                        </div>
                      </div>
                      {match.patient_profile?.care_needs?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {match.patient_profile.care_needs.slice(0, 4).map((n, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] border-cn-border">{n}</Badge>
                          ))}
                          {match.patient_profile.care_needs.length > 4 && (
                            <Badge variant="outline" className="text-[10px] border-cn-border">+{match.patient_profile.care_needs.length - 4}</Badge>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex lg:flex-col items-center gap-4 lg:border-l lg:border-cn-border/50 lg:pl-5">
                      <RingScore value={match.match_score} size={72} label="Fit" />
                      <div className="flex flex-col gap-2">
                        <Button onClick={() => respondToMatch(match.id, 'accepted')} className="cn-btn cn-btn-primary !py-2 !px-4" data-testid={`accept-match-${index}`}>
                          <CheckCircle className="w-4 h-4" /> Accept
                        </Button>
                        <Button onClick={() => respondToMatch(match.id, 'declined')} className="cn-btn cn-btn-ghost !py-2 !px-4 !text-cn-error hover:!bg-cn-error/10" data-testid={`decline-match-${index}`}>
                          <XCircle className="w-4 h-4" /> Decline
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </BentoTile>

        {/* Schedule */}
        <BentoTile span="col-span-full lg:col-span-4" className="p-6" id="schedule" testId="schedule-section">
          <TileHeader eyebrow="Schedule" title="Upcoming visits" />
          {appointments.length === 0 ? (
            <div className="mt-6 text-center py-10 rounded-2xl bg-cn-bg/60 border border-dashed border-cn-border">
              <Calendar className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
              <p className="text-sm text-cn-text-muted">No appointments yet</p>
            </div>
          ) : (
            <div className="mt-5 space-y-3">
              {appointments.slice(0, 5).map((appt, i) => (
                <div key={appt.id} className="flex items-center gap-3 p-3 rounded-2xl border border-cn-border/70 hover:bg-cn-bg/60 transition-colors" data-testid={`appointment-${i}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${appt.appointment_type === 'telehealth' ? 'bg-cn-primary/10 text-cn-primary' : 'bg-cn-secondary/15 text-cn-secondary'}`}>
                    {appt.appointment_type === 'telehealth' ? <Video className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-cn-text dark:text-white text-sm truncate">{appt.patient?.first_name} {appt.patient?.last_name}</p>
                    <p className="text-[11px] text-cn-text-muted">{appt.scheduled_date} · {appt.scheduled_time} · {appt.duration_minutes}m</p>
                  </div>
                  {appt.appointment_type === 'telehealth' && appt.status !== 'completed' && (
                    <Link to={`/telehealth/${appt.id}`}>
                      <Button className="cn-btn cn-btn-primary !py-1.5 !px-3 !text-xs">Start</Button>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </BentoTile>
      </BentoGrid>
    </DashboardShell>
  );
}
